import { Grid } from "@mui/material";
import { useEffectAsync } from "designSystem/hooks";
import { DataGrid, DataGridCardProps, GalleryPlaceholder, TabMargins, ViewSeparator } from "designSystem/index";
import html from "csi/html";
import { ReactViewProps } from "csi/middleware/common";
import DojoView from "csi/page/DojoView";
import session from "csi/session";
import { useStore } from "csi/state/hooks";
import { C8Node, cnl } from "csi/typings";
import csiView from "csi/view";
import xml from "csi/xml";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useState } from "react";
import { usePresentationPageStore } from "./main";
import PPProductCard, { COLUMNS_BY_WIDTH, PPProductCardSkeleton } from "./NewProductPresentation/PPProductCard";
import ProductGalleryStore, { ProductGalleryStoreContext, useProductGalleryStore } from "./NewProductPresentation/ProductGalleryStore";
import { getProductGalleryStore } from "./NewProductPresentation/ProductGalleryStoreService";
import PPProductLayoutStore from "./PPProductLayoutStore";
import { getSetEditStore } from "./PPService";
import SetEditDialog from "./SetEditDialog";
import SetEditErrorDialog from "./SetEditErrorDialog";

const GridCard: FunctionComponent<DataGridCardProps<C8Node>> = observer((props) => {
	const { cache } = useProductGalleryStore();
	const { item, ...otherProps } = props;
	const product = cache.get(item.PPIProduct);

	return (
		<PPProductCard
			colorUrl={item.PPIProductColor}
			item={product}
			{...otherProps}
		/>
	);
});

const GridPlaceholder = () => {
	return (
		<GalleryPlaceholder titleProps={{ res: "PPNoItemsInSet" }} />
	);
};

const SetLayout: FunctionComponent<ReactViewProps> = observer((props) => {
	const { node } = props;
	const rootStore = useStore();
	const { layoutStore } = rootStore;
	const { createProductLayoutStore, isPresentationEditor } = usePresentationPageStore();
	const { cache, viewParams } = layoutStore;
	const setUrl = node.$URL;
	const [productStore, setProductStore] = useState<PPProductLayoutStore>(null);
	const [galleryStore, setGalleryStore] = useState<ProductGalleryStore>(null);
	const [isFailing, setFailing] = useState<boolean>(false);

	const initStore = async () => {
		const store = await getProductGalleryStore(node, cache, onEditSet);
		setGalleryStore(store);
	};

	const onClose = () => {
		setFailing(false);
		setProductStore(null);
	};

	const onEditSet = async (setUrl: cnl): Promise<void> => {
		const currentSetUrl = node.$URL;

		if (!xml.isValidURL(setUrl) || (setUrl === currentSetUrl && isPresentationEditor)) {
			// in the case of hit "Edit Set" button or click the current set, open the edit dialog
			const presentationUrl = node.__Parent__;
			const productStore = await createProductLayoutStore();
			const editSetStore = await getSetEditStore(presentationUrl, currentSetUrl, cache);
			productStore.setFixedEditSetStore(editSetStore);
			setProductStore(productStore);
		} else {
			// if not current set, open another tab
			const url = session.getLocationForUrl(setUrl);
			window.open(url);
		}
	};

	const onSave = async () => {
		try {
			html.showWait(true);
			const status = await productStore.editSetStore.saveChanges();

			if (status) {
				return true;
			} else {
				setFailing(true);
				return false;
			}
		} catch (err) {
			throw err;
		} finally {
			html.showWait(false);

			// reset gallery store and force redraw of properties view even though pdf service fails
			await initStore();
			csiView.getView("Properties")?.redraw();
		}
	};

	useEffectAsync(initStore);

	return (
		<TabMargins>
			<Grid
				container
				direction="column"
				spacing={4}
			>
				<Grid item>
					<DojoView
						nodeUrl={setUrl}
						viewId="Properties"
						viewParams={{
							onEditSet,
							...viewParams,
						}}
					/>
				</Grid>
				<Grid item>
					<ViewSeparator res="ProductPresentationSet.Attr.Items" />
				</Grid>
				<Grid item>
					{galleryStore && (
						<ProductGalleryStoreContext.Provider value={galleryStore}>
							<DataGrid
								CardComponent={GridCard}
								PlaceholderComponent={GridPlaceholder}
								SkeletonComponent={PPProductCardSkeleton}
								columnsByWidth={COLUMNS_BY_WIDTH}
								spacing={3}
								store={galleryStore.store}
							/>
						</ProductGalleryStoreContext.Provider>
					)}
				</Grid>
			</Grid>
			{productStore && (
				<SetEditDialog
					onClose={onClose}
					onSave={onSave}
					open
					productStore={productStore}
				/>
			)}
			{isFailing && (
				<SetEditErrorDialog
					editSetStore={productStore.editSetStore}
					onClose={onClose}
				/>
			)}
		</TabMargins>
	);
});

export default SetLayout;
